/**
 * CardOverviewVertical Component
 *
 * Renders a vertical card layout for credit card offerings.
 * This variation is used for featured cards with vertical orientation,
 * supporting card comparison features and optional promotional badges.
 *
 * Performance optimizations:
 * - Memoized component with React.memo
 * - useMemo for computed values
 * - useCallback for event handlers
 * - useCallback for checkbox change handler
 *
 * @module molecules/CardOverviewVertical
 */

// External library imports
import React, { FC, useRef, useMemo, memo } from 'react';
import clsx from 'clsx';

// Internal library imports
import { RichText, Button } from '@marriott/mi-ui-library';
import { useStore } from '../../modules/store/cobrandStore';
import { CardOverviewVerticalProps } from './CardOverviewVertical.types';
import { StyledCardOverviewVertical } from './CardOverviewVertical.styles';
import { CardArtBadge } from '../CardArtBadge';
import { CardCompareCheckbox } from '../CardCompareCheckbox';
import { COBRAND_CARD_TYPE, LinkType, LinkTarget, ARIA_LABELS, StringBoolean } from '../../modules/constants/constants';
import { CobrandImage } from '../CobrandImage/CobrandImage';
import { CARD_LAYOUT_TYPE } from '../../modules/constants/constants';
import { shouldCardDisplay } from '../../modules/utils';

/**
 * Helper function to determine image CSS class based on layout and card type
 * @param layout - Layout type (e.g., '5-card')
 * @param cardType - Type of card (personal or business)
 * @returns CSS class string for image styling
 */
const getImageCssClass = (
  layout: string | undefined,
  cardType: string,
  isVerticalLayout: boolean,
  isVerticalImage: boolean
): string => {
  const isBusinessCard = cardType === COBRAND_CARD_TYPE.BUSINESS;
  let imageClass;
  if (layout === '5-card') {
    imageClass = 'card-overview-no-border-image';
  } else {
    // Vertical layout always uses the FOC (Featured on Card) image class
    imageClass = isVerticalLayout
      ? 'card-overview-foc-image'
      : `card-overview-blp-image ${isBusinessCard ? 'card-overview-blp-image--margin' : ''}`;
  }

  imageClass = isVerticalImage ? imageClass + ' vertical-image' : imageClass;

  return imageClass;
};

/**
 * Helper function to determine if card art badge should be displayed
 * @param displayCardArtBadge - Whether to display the card art icon
 * @returns Boolean indicating if card art should show
 */
const shouldShowCardArt = (displayCardArtBadge?: boolean): boolean => {
  return displayCardArtBadge?.toString() === 'true';
};

/**
 * CardOverviewVertical Component
 *
 * Displays a credit card offer in vertical layout with:
 * - Card image with optional promotional badge
 * - Card header and description sections
 * - Points/benefits information
 * - Call-to-action button
 * - Optional card comparison checkbox
 *
 * @param props - Component properties
 * @returns JSX element representing the vertical card layout
 */
export const CardOverviewVertical: FC<CardOverviewVerticalProps> = memo<CardOverviewVerticalProps>(props => {
  // Destructure all props for easier access
  const {
    cardType,
    imagePath,
    cardArtIcon,
    cardArtText,
    displayCardArtBadge,
    altText,
    header,
    pointSectionSeparator,
    pointSectionDescription,
    cardSectionSeparator,
    cardSectionDescription,
    linkText,
    linkUrl,
    openInaNewTab,
    componentId,
    customClass,
    styleclass,
    isCardComparisonFeatureEnabled,
    cardCode,
    tertiaryLinks,
    layout,
    trackingProperties,
    variation,
    enableVerticalLayout,
    hasCustomPadding = false,
  } = props;

  // Refs for DOM access
  const buttonRef = useRef<HTMLDivElement>(null);
  const { cobrandData } = useStore(store => store);
  const isVerticalLayout = variation === CARD_LAYOUT_TYPE.VERTICAL;
  const isVerticalImage = enableVerticalLayout;
  // Memoized values for performance optimization
  const isOpenInNewTab = openInaNewTab === true || openInaNewTab === StringBoolean.TRUE;
  const linkType = useMemo<LinkType>(() => (isOpenInNewTab ? LinkType.EXTERNAL : LinkType.INTERNAL), [isOpenInNewTab]);

  const showCardArt = useMemo<boolean>(() => shouldShowCardArt(displayCardArtBadge), [displayCardArtBadge]);
  // Check if this component is inside CobrandContainer with tab functionality
  const isInCobrandContainerWithTabs = props.column === '3';
  const imageCssClass = useMemo<string>(
    () => getImageCssClass(layout, cardType, isVerticalLayout, isVerticalImage),
    [layout, cardType, isVerticalLayout, isVerticalImage]
  );

  // Render vertical card layout
  return (
    <StyledCardOverviewVertical
      data-testid="cobrand-CardOverviewVertical"
      data-component-name="m-cobrand-CardOverviewVertical"
      id={componentId}
      className={clsx(
        'card-overview justify-content-center d-md-flex flex-wrap',
        isVerticalLayout ? 'col-12 p-0' : '',
        styleclass,
        isInCobrandContainerWithTabs && 'd-md-block',
        isInCobrandContainerWithTabs &&
          (shouldCardDisplay(cardType, cobrandData.activeTabValue!) ? 'd-block' : 'd-none')
      )}
      data-cardtype={cardType}
    >
      <div className={clsx(isVerticalLayout ? 'card-overview-foc-wrapper mt-2 mt-md-1 mt-lg-0' : '', 'd-flex')}>
        <div
          className={clsx(
            'card-overview-wrapper justify-content-center d-flex',
            // Conditional spacing based on layout type - remove if hasCustomPadding is true
            !hasCustomPadding && layout !== '5-card' && 'pt-5 pt-md-0',
            isVerticalLayout && 'mt-5 mt-md-3'
          )}
        >
          <div
            className={clsx(
              'd-flex flex-grow-1 justify-content-center',
              layout !== '5-card' && 'mt-0 pt-0 mt-md-5 pt-md-5'
            )}
          >
            <div
              className={clsx(
                isVerticalLayout ? 'px-3 pt-2 pt-md-0' : 'mx-lg-1 card-overview-bordered__default px-md-3 px-2',
                // Base card container styles
                'card-overview-bordered text-center pb-4',
                'd-flex flex-column justify-content-between',
                't-background-color',
                // Separator styles
                (pointSectionSeparator === true ||
                  pointSectionSeparator === StringBoolean.TRUE ||
                  cardSectionSeparator === true ||
                  cardSectionSeparator === StringBoolean.TRUE) &&
                  'card-overview-seperator',
                // 5-card layout specific styles
                layout === '5-card' && 'card-overview-no-border px-lg-0',
                layout === '5-card' && cardType === COBRAND_CARD_TYPE.BUSINESS && 'card-overview-business-card',
                customClass,
                styleclass
              )}
            >
              {/* Main card content wrapper */}
              <div className={clsx('d-flex flex-column flex-grow-1')}>
                {/* Card image section */}
                <div
                  className={clsx(
                    isVerticalLayout ? 'px-0 card-overview-bordered__image' : '',
                    'px-lg-0 mx-auto mt-1 mt-sm-2'
                  )}
                >
                  {showCardArt ? (
                    <CardArtBadge
                      imagePath={imagePath}
                      imageClass="col-12 p-0"
                      badgeClass="mx-2"
                      defaultImageText={altText}
                      badgeDetails={`<div class='d-flex align-items-center justify-content-center badge-txt-align'>
                          <div><span class="${cardArtIcon ? `${cardArtIcon.split(':')[1]} m-0 pr-1` : ''}"></span></div>
                          <div class='t-font-xs t-overline-overlay'>${cardArtText?.toUpperCase() || ''}</div>
                        </div>`}
                      customClass={imageCssClass}
                    />
                  ) : (
                    <CobrandImage defaultImageURL={imagePath} altText={altText} customClass={imageCssClass} />
                  )}
                </div>

                {/* Card header section */}
                {header && (
                  <RichText
                    componentId={`header-${componentId}`}
                    text={header}
                    customClass={clsx(
                      'mx-auto mt-3',
                      isVerticalLayout ? 'card-overview-foc-header' : 'card-overview-header'
                    )}
                  />
                )}

                {/* Points section separator */}
                {(pointSectionSeparator === true || pointSectionSeparator === StringBoolean.TRUE) && (
                  <hr className="mb-3 mx-0 row" />
                )}

                {/* Points section description */}
                {pointSectionDescription && (
                  <RichText
                    componentId={`pointsSection-${componentId}`}
                    text={pointSectionDescription}
                    customClass={clsx(
                      'd-flex flex-column margin-zero card-overview-subheader',
                      isVerticalLayout ? 'card-overview-foc flex-grow-1' : ''
                    )}
                  />
                )}

                {/* Card section separator */}
                {(cardSectionSeparator === true || cardSectionSeparator === StringBoolean.TRUE) && (
                  <hr className="mt-3 mb-4 mx-0 row" />
                )}

                {/* Card section description */}
                {cardSectionDescription && (
                  <RichText
                    componentId={`cardSection-${componentId}`}
                    text={cardSectionDescription}
                    customClass="d-flex flex-column flex-grow-1"
                  />
                )}

                {/* Tertiary links section */}
                {tertiaryLinks && (
                  <RichText
                    componentId={`tertiaryLinks-${componentId}`}
                    customClass="tertiaryLinks_styles"
                    text={tertiaryLinks}
                  />
                )}
              </div>

              {/* Call-to-action button */}
              <div>
                <Button
                  id={`${componentId}-btn`}
                  isLink
                  href={linkUrl}
                  target={isOpenInNewTab ? LinkTarget.BLANK : LinkTarget.SELF}
                  className={clsx('m-button-m mt-md-3 mb-0 mt-0', isVerticalLayout ? 'mt-2 mt-lg-0' : '')}
                  buttonCopy={linkText}
                  linkType={linkType}
                  ref={buttonRef}
                  linkAriaLabelOpenNewWindow={isOpenInNewTab ? ARIA_LABELS.OPEN_NEW_TAB : ''}
                  trackingProperties={trackingProperties}
                />
              </div>

              {/* Card comparison checkbox (optional feature) - using CardCompareCheckbox molecule */}
              {isCardComparisonFeatureEnabled && (
                <CardCompareCheckbox cardCode={cardCode || ''} componentId={componentId} customClass={'mt-4'} />
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledCardOverviewVertical>
  );
});

CardOverviewVertical.displayName = 'CardOverviewVertical';
